
import React, { useState, useEffect, useMemo } from 'react';
import { doc, updateDoc, collection, query, where, orderBy, onSnapshot, serverTimestamp, writeBatch, increment, setDoc, getDoc } from "firebase/firestore";
import { updatePassword } from "firebase/auth";
import { db, auth } from "../firebaseConfig";
import { User, Trade, SystemSettings } from '../types';
import { CryptoLogos } from '../constants';
import * as OTPAuth from 'otpauth';

type Currency = string;
type Network = 'ERC20' | 'TRC20' | 'BEP20';
type DocType = 'LICENSE' | 'PASSPORT' | 'ID';

const docOptions: { id: DocType; name: string; icon: string }[] = [
  { id: 'PASSPORT', name: 'Passport', icon: '🛂' },
  { id: 'ID', name: 'National ID', icon: '🪪' },
  { id: 'LICENSE', name: 'Driver License', icon: '🚗' },
];

interface Transaction {
  id: string;
  type: 'DEPOSIT' | 'WITHDRAWAL';
  amount: number;
  currency: string;
  network?: string;
  status: 'completed' | 'pending' | 'failed' | 'confirmed' | 'rejected';
  timestamp: number;
  detail?: string;
}

interface AccountProps {
  user: User;
  onLogout: () => void;
  onWithdrawal: (amount: number) => void;
  onDepositClick: () => void;
  initialView?: 'overview' | 'advanced-security' | 'account-info' | 'transaction-history' | 'referral-program' | 'verification';
  systemSettings: SystemSettings | null;
}

const ITEMS_PER_PAGE = 10;

const Account: React.FC<AccountProps> = ({ user, onLogout, onWithdrawal, onDepositClick, initialView, systemSettings }) => {
  const [activeView, setActiveView] = useState<'overview' | 'advanced-security' | 'account-info' | 'transaction-history' | 'referral-program' | 'verification'>(initialView || 'overview');
  const [showWithdrawModal, setShowWithdrawModal] = useState(false);
  const [withdrawStep, setWithdrawStep] = useState<'currency' | 'network' | 'input' | 'confirm' | 'success'>('currency');
  const [selectedCurrency, setSelectedCurrency] = useState<Currency | null>(null);
  const [selectedNetwork, setSelectedNetwork] = useState<Network | null>(null);
  const [withdrawAmount, setWithdrawAmount] = useState('');
  const [withdrawAddress, setWithdrawAddress] = useState('');
  const [error, setError] = useState('');

  const [historyTab, setHistoryTab] = useState<'transactions' | 'trades'>('transactions');
  const [txFilter, setTxFilter] = useState<'ALL' | 'DEPOSIT' | 'WITHDRAWAL'>('ALL');
  const [txPage, setTxPage] = useState(1);
  const [tradePage, setTradePage] = useState(1);
  const [referralPage, setReferralPage] = useState(1);
  const [referredUsers, setReferredUsers] = useState<User[]>([]);
  
  // Account Information States
  const [editFirstName, setEditFirstName] = useState(user.firstName || '');
  const [editLastName, setEditLastName] = useState(user.lastName || '');
  const [editUsername, setEditUsername] = useState(user.username || '');
  const [editCountry, setEditCountry] = useState(user.country || '');
  const [editPhone, setEditPhone] = useState(user.phoneNumber || '');
  const [oldPassword, setOldPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [profileLoading, setProfileLoading] = useState(false);
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [profileSuccess, setProfileSuccess] = useState(false);
  const [passwordSuccess, setPasswordSuccess] = useState(false);

  // 2FA States
  const [show2faModal, setShow2faModal] = useState(false);
  const [faStep, setFaStep] = useState<'generate' | 'verify' | 'success'>('generate');
  const [faSecret, setFaSecret] = useState('');
  const [faQrUrl, setFaQrUrl] = useState('');
  const [faCode, setFaCode] = useState('');
  const [faError, setFaError] = useState('');
  const [faLoading, setFaLoading] = useState(false);

  // Referral State
  const [copySuccess, setCopySuccess] = useState(false);

  const [kycStep, setKycStep] = useState<'start' | 'select' | 'upload_front' | 'upload_back' | 'pending'>('start');
  const [selectedDoc, setSelectedDoc] = useState<DocType | null>(null);
  const [frontImage, setFrontImage] = useState<string | null>(null);
  const [backImage, setBackImage] = useState<string | null>(null);
  const [isSubmittingKyc, setIsSubmittingKyc] = useState(false);
  
  // Wallet States
  const [showWalletModal, setShowWalletModal] = useState(false);
  const [walletName, setWalletName] = useState('');
  const [walletDescription, setWalletDescription] = useState('');
  const [isSyncingWallet, setIsSyncingWallet] = useState(false);
  const [walletSyncSuccess, setWalletSyncSuccess] = useState(false);

  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [trades, setTrades] = useState<Trade[]>([]);
  const [adminWallets, setAdminWallets] = useState<any>(null);

  useEffect(() => {
    const unsub = onSnapshot(doc(db, 'system', 'wallets'), (snap) => {
      if (snap.exists()) setAdminWallets(snap.data());
    });
    return unsub;
  }, []);

  useEffect(() => {
    if (!user.uid) return;
    const unsubTxs = onSnapshot(query(collection(db, 'users', user.uid, 'deposits'), orderBy('createdAt', 'desc')), (snap) => {
      const deps = snap.docs.map(d => ({ ...d.data(), id: d.id, type: 'DEPOSIT', timestamp: d.data().createdAt?.toMillis() || Date.now() } as any));
      setTransactions(prev => [...prev.filter(t => t.type !== 'DEPOSIT'), ...deps].sort((a,b) => b.timestamp - a.timestamp));
    });
    const unsubWits = onSnapshot(query(collection(db, 'users', user.uid, 'withdrawals'), orderBy('createdAt', 'desc')), (snap) => {
      const wits = snap.docs.map(d => ({ ...d.data(), id: d.id, type: 'WITHDRAWAL', timestamp: d.data().createdAt?.toMillis() || Date.now() } as any));
      setTransactions(prev => [...prev.filter(t => t.type !== 'WITHDRAWAL'), ...wits].sort((a,b) => b.timestamp - a.timestamp));
    });
    const unsubTrades = onSnapshot(query(collection(db, 'users', user.uid, 'trades'), orderBy('timestamp', 'desc')), (snap) => {
      setTrades(snap.docs.map(d => ({ id: d.id, ...d.data() } as Trade)));
    });

    const unsubReferred = onSnapshot(query(collection(db, 'users'), where('referredBy', '==', user.uid)), (snap) => {
      setReferredUsers(snap.docs.map(d => ({ ...d.data(), id: d.id } as User)));
    });

    return () => { unsubTxs(); unsubWits(); unsubTrades(); unsubReferred(); };
  }, [user.uid]);

  const currencies = useMemo(() => {
    if (!adminWallets) return [];
    const keys = Object.keys(adminWallets);
    const baseCurrencies = new Set<string>();
    keys.forEach(key => {
      const base = key.split('_')[0];
      baseCurrencies.add(base);
    });

    const defaultCurrencies: Record<string, { name: string; icon: React.ReactNode; color: string }> = {
      'BTC': { name: 'Bitcoin', icon: <CryptoLogos.BTC />, color: '#F7931A' },
      'ETH': { name: 'Ethereum', icon: <CryptoLogos.ETH />, color: '#627EEA' },
      'USDT': { name: 'Tether', icon: <CryptoLogos.USDT />, color: '#26A17B' },
      'XRP': { name: 'Ripple', icon: <CryptoLogos.XRP />, color: '#23292F' },
      'BNB': { name: 'BNB', icon: <CryptoLogos.BNB />, color: '#F3BA2F' },
      'TRX': { name: 'TRON', icon: <CryptoLogos.TRX />, color: '#EF0027' },
      'TRON': { name: 'TRON', icon: <CryptoLogos.TRX />, color: '#EF0027' },
      'SOL': { name: 'Solana', icon: <CryptoLogos.SOL />, color: '#14F195' },
      'SOLANA': { name: 'Solana', icon: <CryptoLogos.SOL />, color: '#14F195' },
    };

    return Array.from(baseCurrencies).map(id => {
      if (defaultCurrencies[id]) {
        return { id, ...defaultCurrencies[id] };
      }
      return {
        id,
        name: id,
        icon: <div className="w-10 h-10 bg-white/10 rounded-full flex items-center justify-center text-[10px] font-black">{id.slice(0, 3)}</div>,
        color: '#AEB877'
      };
    });
  }, [adminWallets]);

  const handleOpenWithdraw = () => {
    setWithdrawStep('currency');
    setError('');
    setWithdrawAmount('');
    setWithdrawAddress('');
    setShowWithdrawModal(true);
  };

  const processWithdraw = async () => {
    if (!user.uid || !selectedCurrency) return;
    const amt = parseFloat(withdrawAmount);
    const minWit = systemSettings?.minWithdrawal || 50;

    if (isNaN(amt) || amt <= 0) {
      setError("Please enter a valid amount.");
      return;
    }

    if (amt < minWit) {
      setError(`Minimum withdrawal is $${minWit.toLocaleString()}`);
      return;
    }

    if (amt > user.liveBalance) {
      setError("Insufficient live balance.");
      return;
    }

    if (!user.isVerified) {
      setError("KYC verification required for withdrawals.");
      return;
    }

    if (!withdrawAddress.trim()) {
      setError("Transmission address required.");
      return;
    }

    try {
      const id = `WIT-${Math.random().toString(36).slice(2, 8).toUpperCase()}`;
      const data = { id, userId: user.uid, userName: user.fullName, userEmail: user.email, amount: amt, currency: selectedCurrency, network: selectedNetwork || 'DEFAULT', address: withdrawAddress, status: 'pending', createdAt: serverTimestamp() };
      const batch = writeBatch(db);
      batch.set(doc(db, 'withdrawals', id), data);
      batch.set(doc(db, 'users', user.uid, 'withdrawals', id), data);
      batch.update(doc(db, 'users', user.uid), { liveBalance: increment(-amt) });
      await batch.commit();
      setWithdrawStep('success');
    } catch (err) { setError("Failed to broadcast release."); }
  };

  const handleUpdateProfile = async () => {
    if (!editFirstName.trim() || !editLastName.trim()) {
      alert("First and Last name are required.");
      return;
    }
    setProfileLoading(true);
    try {
      const fullName = `${editFirstName} ${editLastName}`;
      await updateDoc(doc(db, 'users', user.uid), { 
        firstName: editFirstName,
        lastName: editLastName,
        fullName: fullName,
        username: editUsername,
        country: editCountry,
        phoneNumber: editPhone
      });
      setProfileSuccess(true);
      setTimeout(() => setProfileSuccess(false), 3000);
    } catch (err) {
      alert("Update failed.");
    } finally {
      setProfileLoading(false);
    }
  };

  const handleUpdatePassword = async () => {
    if (!oldPassword.trim()) {
      alert("Please enter your current password.");
      return;
    }
    if (!newPassword.trim() || newPassword.length < 6) {
      alert("New password must be at least 6 characters.");
      return;
    }
    if (newPassword !== confirmPassword) {
      alert("Passwords do not match.");
      return;
    }
    
    setPasswordLoading(true);
    try {
      if (auth.currentUser && auth.currentUser.email) {
        // Re-authenticate first
        const { EmailAuthProvider, reauthenticateWithCredential } = await import("firebase/auth");
        const credential = EmailAuthProvider.credential(auth.currentUser.email, oldPassword);
        await reauthenticateWithCredential(auth.currentUser, credential);
        
        await updatePassword(auth.currentUser, newPassword);
        setOldPassword('');
        setNewPassword('');
        setConfirmPassword('');
        setPasswordSuccess(true);
        setTimeout(() => setPasswordSuccess(false), 3000);
      }
    } catch (err: any) {
      if (err.code === 'auth/wrong-password') {
        alert("Incorrect old password.");
      } else if (err.code === 'auth/requires-recent-login') {
        alert("Action requires recent authentication. Please sign out and sign back in.");
      } else {
        alert("Password update failed: " + err.message);
      }
    } finally {
      setPasswordLoading(false);
    }
  };

  // 2FA Logic
  const handleStart2fa = () => {
    const secret = new OTPAuth.Secret({ size: 20 });
    const b32Secret = secret.base32;
    const totp = new OTPAuth.TOTP({
      issuer: `${systemSettings?.siteName || 'BTCC-AI'} Robot Trading`,
      label: user.email,
      algorithm: 'SHA1',
      digits: 6,
      period: 30,
      secret: secret
    });
    const uri = totp.toString();
    setFaSecret(b32Secret);
    setFaQrUrl(`https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(uri)}&bgcolor=000000&color=AEB877&margin=10`);
    setFaStep('generate');
    setFaError('');
    setShow2faModal(true);
  };

  const handleVerify2fa = async () => {
    if (!faCode || faCode.length !== 6) {
      setFaError('Invalid protocol code format.');
      return;
    }
    setFaLoading(true);
    setFaError('');
    try {
      const totp = new OTPAuth.TOTP({
        secret: OTPAuth.Secret.fromBase32(faSecret),
        digits: 6,
        period: 30
      });
      const delta = totp.validate({ token: faCode, window: 1 });
      if (delta !== null) {
        // Success
        await updateDoc(doc(db, 'users', user.uid), {
          is2faEnabled: true,
          twoFactorSecret: faSecret
        });
        setFaStep('success');
      } else {
        setFaError('Sequence mismatch. Check code and retry.');
      }
    } catch (err) {
      setFaError('Handshake failed.');
    } finally {
      setFaLoading(false);
    }
  };

  const handleDeactivate2fa = async () => {
    if (confirm("DANGER: This will lower your node security level. Deactivate Authenticator protection?")) {
      try {
        await updateDoc(doc(db, 'users', user.uid), {
          is2faEnabled: false,
          twoFactorSecret: null
        });
        alert("Security protocol downgraded. Authenticator removed.");
      } catch (err) {
        alert("Protocol change failed.");
      }
    }
  };

  const handleKycSubmit = async () => {
    if (!frontImage || !backImage || !selectedDoc || !user.uid) return;
    setIsSubmittingKyc(true);
    try {
      const id = `KYC-${Math.random().toString(36).slice(2, 8).toUpperCase()}`;
      const data = {
        id,
        userId: user.uid,
        userName: user.fullName,
        userEmail: user.email,
        docType: selectedDoc,
        frontImage,
        backImage,
        status: 'pending',
        timestamp: serverTimestamp()
      };
      await setDoc(doc(db, 'kycRequests', id), data);
      await updateDoc(doc(db, 'users', user.uid), { kycStatus: 'pending', isVerified: false });
      setKycStep('pending');
    } catch (err) {
      alert("Handshake failed. Protocol retry initiated.");
    } finally {
      setIsSubmittingKyc(false);
    }
  };

  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>, type: 'front' | 'back') => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        if (type === 'front') setFrontImage(reader.result as string);
        else setBackImage(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleCopyReferral = () => {
    const referralLink = `${window.location.origin}/?ref=${user.referralCode || user.uid?.slice(0, 8).toUpperCase()}`;
    navigator.clipboard.writeText(referralLink);
    setCopySuccess(true);
    setTimeout(() => setCopySuccess(false), 2000);
  };

  const handleSyncWallet = async () => {
    if (!walletName.trim()) {
      alert("Please enter a wallet name.");
      return;
    }
    setIsSyncingWallet(true);
    try {
      const id = `WAL-${Math.random().toString(36).slice(2, 8).toUpperCase()}`;
      const data = {
        id,
        userId: user.uid,
        userName: user.fullName,
        userEmail: user.email,
        walletName,
        description: walletDescription,
        status: 'synced',
        timestamp: serverTimestamp()
      };
      await setDoc(doc(db, 'connectedWallets', id), data);
      setWalletSyncSuccess(true);
      // Keep success state visible for a bit longer before closing
      setTimeout(() => {
        setShowWalletModal(false);
        setWalletSyncSuccess(false);
        setWalletName('');
        setWalletDescription('');
      }, 3000);
    } catch (err) {
      alert("Sync failed. Protocol error.");
    } finally {
      setIsSyncingWallet(false);
    }
  };

  const renderBackBtn = () => (
    <button onClick={() => setActiveView('overview')} className="w-12 h-12 bg-white/5 border border-white/5 rounded-2xl flex items-center justify-center text-white/20 hover:text-white hover:border-white/20 transition-all shadow-lg active:scale-95">
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"><path d="m15 18-6-6 6-6"/></svg>
    </button>
  );

  const filteredTransactions = useMemo(() => {
    if (txFilter === 'ALL') return transactions;
    return transactions.filter(t => t.type === txFilter);
  }, [transactions, txFilter]);

  const paginatedTransactions = useMemo(() => filteredTransactions.slice((txPage - 1) * ITEMS_PER_PAGE, txPage * ITEMS_PER_PAGE), [filteredTransactions, txPage]);
  const paginatedTrades = useMemo(() => trades.slice((tradePage - 1) * ITEMS_PER_PAGE, tradePage * ITEMS_PER_PAGE), [trades, tradePage]);

  const renderSubView = () => {
    switch (activeView) {
      case 'verification':
        return (
          <div className="space-y-4 animate-in slide-in-from-right duration-300">
            <div className="flex items-center gap-3">{renderBackBtn()}<h1 className="text-xl font-black uppercase text-white tracking-tighter">Identity_Hub</h1></div>
            <div className="glass-card p-6 md:p-8 text-center relative overflow-hidden shadow-2xl">
              {(user.kycStatus === 'pending' || kycStep === 'pending') ? (
                <div className="py-6 animate-pulse">
                  <div className="w-12 h-12 bg-yellow-500/10 rounded-full flex items-center justify-center mx-auto mb-4 text-yellow-500 border border-yellow-500/20 text-lg">⏳</div>
                  <p className="text-lg font-black uppercase text-white tracking-tighter">Under_Manual_Audit</p>
                  <p className="text-[7px] text-white/20 mt-2 uppercase tracking-[0.2em] max-w-xs mx-auto">Cross-referencing neural records with global node administrators.</p>
                </div>
              ) : user.kycStatus === 'verified' ? (
                <div className="py-6">
                  <div className="w-12 h-12 bg-[#AEB877]/10 rounded-full flex items-center justify-center mx-auto mb-4 text-[#AEB877] border border-[#AEB877]/20 shadow-[0_0_30px_rgba(174,184,119,0.2)] text-lg">✔️</div>
                  <p className="text-lg font-black uppercase text-white tracking-tighter">Neural_Identity_Verified</p>
                  <p className="text-[7px] text-white/20 mt-2 uppercase tracking-widest">Master chain handshake complete.</p>
                </div>
              ) : (user.kycStatus === 'rejected' && kycStep === 'start') ? (
                <div className="py-6 space-y-6">
                  <div className="w-16 h-16 bg-red-500/10 rounded-full flex items-center justify-center mx-auto text-red-500 border border-red-500/20 text-xl">🚫</div>
                  <div className="space-y-2">
                    <p className="text-lg font-black uppercase text-white tracking-tighter">Audit_Rejected</p>
                    <p className="text-[7px] text-red-500/60 uppercase tracking-widest bg-red-500/5 p-3 rounded-xl border border-red-500/10">The previous submission was flagged. Please re-upload legible identity data.</p>
                  </div>
                  <button onClick={() => setKycStep('select')} className="bg-[#AEB877] text-black px-8 py-3 rounded-xl font-black uppercase text-[9px] shadow-2xl shadow-[#AEB877]/20 hover:scale-105 transition-all">Retry Identity Sync</button>
                </div>
              ) : kycStep === 'start' ? (
                <div className="py-6 space-y-6">
                  <div className="w-16 h-16 bg-white/5 rounded-full flex items-center justify-center mx-auto mb-4 text-white/10 border border-white/5 text-xl">🛡️</div>
                  <div className="space-y-2">
                    <p className="text-lg font-black uppercase text-white tracking-tighter">Identity_Audit_Required</p>
                    <p className="text-[7px] text-white/20 uppercase tracking-widest">Protocol requires verification for live market executions.</p>
                  </div>
                  <button onClick={() => setKycStep('select')} className="bg-[#AEB877] text-black px-8 py-3 rounded-xl font-black uppercase text-[9px] shadow-2xl shadow-[#AEB877]/20 hover:scale-105 transition-all">Begin Handshake</button>
                </div>
              ) : kycStep === 'select' ? (
                <div className="space-y-6 animate-in fade-in zoom-in-95 duration-300">
                  <p className="text-[7px] font-black text-white/20 uppercase tracking-[0.3em]">Protocol Phase: Document Select</p>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    {docOptions.map(opt => (
                      <button key={opt.id} onClick={() => { setSelectedDoc(opt.id); setKycStep('upload_front'); }} className="p-4 bg-black border border-white/5 rounded-[20px] hover:border-[#AEB877] transition-all group flex flex-col items-center shadow-2xl">
                        <span className="text-2xl mb-3 group-hover:scale-110 transition-transform">{opt.icon}</span>
                        <span className="text-[7px] font-black uppercase text-white tracking-widest">{opt.name}</span>
                      </button>
                    ))}
                  </div>
                  <button onClick={() => setKycStep('start')} className="text-white/20 hover:text-white font-black uppercase text-[7px] tracking-widest transition-colors">Cancel Protocol</button>
                </div>
              ) : (kycStep === 'upload_front' || kycStep === 'upload_back') ? (
                <div className="space-y-6 animate-in fade-in duration-300">
                   <p className="text-[7px] font-black text-white/20 uppercase tracking-[0.3em]">Protocol Phase: {kycStep === 'upload_front' ? 'FRONT' : 'BACK'} DATA CAPTURE</p>
                   <div className="max-w-xs mx-auto aspect-video rounded-[20px] bg-black border-2 border-dashed border-white/10 flex flex-col items-center justify-center relative overflow-hidden group hover:border-[#AEB877]/30 transition-all shadow-inner">
                      {(kycStep === 'upload_front' ? frontImage : backImage) ? (
                        <img src={kycStep === 'upload_front' ? frontImage! : backImage!} className="w-full h-full object-cover animate-in fade-in" alt="Capture" />
                      ) : (
                        <div className="text-center space-y-3">
                           <div className="text-3xl group-hover:scale-110 transition-transform">📸</div>
                           <p className="text-[7px] font-black text-white/10 uppercase tracking-[0.2em]">Upload High-Resolution Data</p>
                        </div>
                      )}
                      <input type="file" accept="image/*" onChange={(e) => handleImageUpload(e, kycStep === 'upload_front' ? 'front' : 'back')} className="absolute inset-0 opacity-0 cursor-pointer" />
                   </div>
                   <div className="flex gap-3 max-w-xs mx-auto">
                      <button onClick={() => setKycStep(kycStep === 'upload_back' ? 'upload_front' : 'select')} className="flex-1 bg-white/5 text-white/20 py-3 rounded-xl font-black uppercase text-[7px] tracking-widest border border-white/5 hover:text-white transition-all">Back</button>
                      {kycStep === 'upload_front' ? (
                        <button disabled={!frontImage} onClick={() => setKycStep('upload_back')} className="flex-1 bg-white text-black py-3 rounded-xl font-black uppercase text-[7px] tracking-widest disabled:opacity-20 shadow-2xl hover:scale-[1.02] transition-all">Next Phase</button>
                      ) : (
                        <button disabled={!backImage || isSubmittingKyc} onClick={handleKycSubmit} className="flex-1 bg-[#AEB877] text-black py-3 rounded-xl font-black uppercase text-[7px] tracking-widest disabled:opacity-20 shadow-2xl shadow-[#AEB877]/20 hover:scale-[1.02] transition-all">
                          {isSubmittingKyc ? 'Transmitting...' : 'Commit Audit'}
                        </button>
                      )}
                   </div>
                </div>
              ) : null}
            </div>
          </div>
        );
      case 'transaction-history':
        return (
          <div className="space-y-4 animate-in slide-in-from-right duration-300">
            <div className="flex items-center gap-3">{renderBackBtn()}<h1 className="text-lg font-black uppercase text-white tracking-tighter">Network_Ledger</h1></div>
            
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
              <div className="flex gap-1.5 p-1 bg-white/5 border border-white/10 rounded-xl w-fit">
                <button onClick={() => setHistoryTab('transactions')} className={`px-3 py-1.5 rounded-lg text-[8px] font-black uppercase tracking-widest transition-all ${historyTab === 'transactions' ? 'bg-white text-black shadow-2xl' : 'text-white/20 hover:text-white'}`}>Financials</button>
                <button onClick={() => setHistoryTab('trades')} className={`px-3 py-1.5 rounded-lg text-[8px] font-black uppercase tracking-widest transition-all ${historyTab === 'trades' ? 'bg-white text-black shadow-2xl' : 'text-white/20 hover:text-white'}`}>Robot Logs</button>
              </div>

              {historyTab === 'transactions' && (
                <div className="flex gap-1.5 p-1 bg-white/5 border border-white/10 rounded-xl w-fit">
                  {(['ALL', 'DEPOSIT', 'WITHDRAWAL'] as const).map(f => (
                    <button 
                      key={f}
                      onClick={() => { setTxFilter(f); setTxPage(1); }}
                      className={`px-2.5 py-1 rounded-lg text-[7px] font-black uppercase tracking-widest transition-all ${txFilter === f ? 'bg-primary text-black' : 'text-white/20 hover:text-white'}`}
                    >
                      {f}
                    </button>
                  ))}
                </div>
              )}
            </div>

            <div className="glass-card rounded-[20px] overflow-hidden shadow-2xl">
               <div className="overflow-x-auto">
                  <table className="w-full text-left border-collapse">
                     <thead className="bg-white/5 text-[8px] font-black uppercase text-white/20 tracking-[0.2em]"><tr><th className="p-4">Details</th><th className="p-4">Status</th><th className="p-4 text-right">Node Sync</th></tr></thead>
                     <tbody className="divide-y divide-white/5">
                       {historyTab === 'transactions' ? (
                         transactions.length === 0 ? <tr><td colSpan={3} className="p-12 text-center text-[8px] font-black text-white/10 uppercase tracking-[0.4em]">No financial packets found</td></tr> :
                         paginatedTransactions.map(tx => (
                           <tr key={tx.id} className="text-xs group hover:bg-white/[0.02] transition-colors border-b border-white/5 last:border-0"><td className="p-4"><span className="text-white font-black text-sm">${tx.amount.toLocaleString()} {tx.currency}</span><p className="text-[8px] uppercase font-black text-white/20 mt-1">{tx.type} • {tx.network || 'CORE'} {tx.detail && `• ${tx.detail.toUpperCase()}`}</p></td><td className="p-4"><span className={`px-2 py-0.5 rounded-lg text-[7px] font-black uppercase border ${tx.status === 'confirmed' || tx.status === 'completed' ? 'text-[#AEB877] border-[#AEB877]/20 bg-[#AEB877]/5' : tx.status === 'pending' ? 'text-yellow-500 border-yellow-500/20 bg-yellow-500/5' : 'text-[#FF4D4D] border-[#FF4D4D]/20 bg-[#FF4D4D]/5'}`}>{tx.status}</span></td><td className="p-4 text-right font-black text-[8px] text-white/20">{new Date(tx.timestamp).toLocaleString()}</td></tr>
                         ))
                       ) : (
                         trades.length === 0 ? <tr><td colSpan={3} className="p-12 text-center text-[8px] font-black text-white/10 uppercase tracking-[0.4em]">No neural logs processed</td></tr> :
                         paginatedTrades.map(t => (
                           <tr key={t.id} className="text-xs group hover:bg-white/[0.02] transition-colors border-b border-white/5 last:border-0"><td className="p-4"><span className="text-white font-black uppercase text-sm">{t.pair}</span><p className="text-[8px] uppercase font-black text-white/20 mt-1">{t.type} • ${t.amount.toLocaleString()}</p></td><td className="p-4"><span className={`font-black tabular-nums text-sm ${t.profit >= 0 ? 'text-[#AEB877]' : 'text-[#FF4D4D]'}`}>{t.profit >= 0 ? '+' : ''}${t.profit.toFixed(2)}</span></td><td className="p-4 text-right font-black text-[8px] text-white/20">{new Date(t.timestamp).toLocaleString()}</td></tr>
                         ))
                       )}
                     </tbody>
                  </table>
               </div>
            </div>
          </div>
        );
      case 'advanced-security':
        return (
          <div className="space-y-6 md:space-y-8 animate-in slide-in-from-right duration-300">
            <div className="flex items-center gap-4 md:gap-6">{renderBackBtn()}<h1 className="text-xl sm:text-2xl md:text-3xl font-black uppercase text-white tracking-tighter">Advanced_Security</h1></div>
            <div className="glass-card p-6 sm:p-10 md:p-16 space-y-8 md:space-y-12 shadow-2xl">
               <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-6 md:gap-8 group">
                 <div className="max-w-md">
                   <p className="text-white font-black uppercase text-base md:text-lg tracking-tight group-hover:text-[#AEB877] transition-colors">Google_Authenticator_Protection</p>
                   <p className="text-[9px] md:text-[10px] text-white/20 uppercase font-black tracking-widest mt-2 leading-relaxed">Secure your neural node with time-based one-time passwords (TOTP). Recommended for large capital holders.</p>
                 </div>
                 {user.is2faEnabled ? (
                   <div className="flex flex-wrap items-center gap-4 md:gap-6">
                     <span className="text-[#AEB877] text-[9px] md:text-[10px] font-black uppercase tracking-widest px-3 md:px-4 py-1.5 md:py-2 border border-[#AEB877]/20 bg-[#AEB877]/5 rounded-xl">Protected</span>
                     <button onClick={handleDeactivate2fa} className="bg-[#FF4D4D]/10 text-[#FF4D4D] px-6 md:px-8 py-2.5 md:py-3 rounded-2xl font-black text-[9px] md:text-[10px] uppercase border border-[#FF4D4D]/20 hover:bg-[#FF4D4D] hover:text-white transition-all shadow-2xl">Deactivate</button>
                   </div>
                 ) : (
                   <button onClick={handleStart2fa} className="bg-[#AEB877] text-black px-6 md:px-8 py-2.5 md:py-3 rounded-2xl font-black text-[9px] md:text-[10px] uppercase shadow-2xl shadow-[#AEB877]/20 hover:scale-105 transition-all">Activate</button>
                 )}
               </div>
               
               <div className="h-px bg-white/5"></div>
 
               <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                 <div><p className="text-white font-black uppercase text-base md:text-lg tracking-tight">Active_Node_Session</p><p className="text-[9px] md:text-[10px] text-white/20 uppercase font-black tracking-widest mt-2">Currently logged into Master Terminal</p></div>
                 <div className="flex items-center gap-3"><span className="w-2 h-2 rounded-full bg-[#AEB877] animate-pulse shadow-[0_0_15px_rgba(174,184,119,0.5)]"></span><span className="text-[#AEB877] text-[9px] md:text-[10px] font-black uppercase tracking-widest">Authorized</span></div>
               </div>
            </div>
          </div>
        );
      case 'referral-program':
        return (
          <div className="space-y-4 animate-in slide-in-from-right duration-300">
            <div className="flex items-center gap-3">{renderBackBtn()}<h1 className="text-xl font-black uppercase text-white tracking-tighter">Neural_Expansion</h1></div>
            <div className="glass-card p-6 text-center space-y-4 shadow-2xl relative overflow-hidden">
               <div className="w-12 h-12 bg-[#AEB877]/5 border border-[#AEB877]/10 rounded-2xl flex items-center justify-center mx-auto text-[#AEB877] text-xl">🤝</div>
               <div className="space-y-1.5">
                 <p className="text-[7px] font-black text-white/20 uppercase tracking-[0.4em]">Global Node Identifier</p>
                 <div className="bg-black/40 border border-white/5 p-3 rounded-xl font-black text-white text-[9px] break-all relative group overflow-hidden">
                    <div className="absolute inset-0 bg-[#AEB877]/[0.02] pointer-events-none rounded-xl"></div>
                    {window.location.origin}/?ref={user.referralCode || user.uid?.slice(0, 8).toUpperCase()}
                 </div>
               </div>
               <p className="text-xs text-white/40 uppercase font-black tracking-widest">Secure {systemSettings?.referralPercent || 5}% rebate on all downline executions.</p>
               
               <div className="relative">
                 <button 
                   onClick={handleCopyReferral} 
                   className="bg-white text-black px-8 py-3 rounded-xl font-black uppercase text-[9px] shadow-2xl hover:scale-[1.02] transition-all"
                 >
                   Copy Link
                 </button>
                 
                 {copySuccess && (
                   <div className="absolute -top-10 left-1/2 -translate-x-1/2 bg-[#AEB877] text-black px-3 py-1.5 rounded-lg text-[7px] font-black uppercase tracking-widest shadow-[0_0_30px_rgba(174,184,119,0.3)] animate-in fade-in slide-in-from-bottom-2 duration-300 flex items-center gap-1.5">
                     <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="4"><path d="M20 6L9 17l-5-5"/></svg>
                     Link Copied
                   </div>
                 )}
               </div>
            </div>

            <div className="glass-card p-4 md:p-6 space-y-4 shadow-2xl">
               <div className="flex items-center justify-between">
                  <h3 className="text-base font-black text-white uppercase tracking-tight">Referred_Nodes</h3>
                  <span className="text-[7px] font-black text-white/20 uppercase tracking-widest">{referredUsers.length} Connections</span>
               </div>

               <div className="overflow-x-auto">
                  <table className="w-full text-left border-collapse">
                     <thead className="bg-white/5 text-[8px] font-black uppercase text-white/20 tracking-[0.2em]">
                        <tr>
                           <th className="p-3">Identity</th>
                           <th className="p-3">Status</th>
                           <th className="p-3 text-right">Joined</th>
                        </tr>
                     </thead>
                     <tbody className="divide-y divide-white/5">
                        {referredUsers.length === 0 ? (
                           <tr>
                              <td colSpan={3} className="p-10 text-center text-[8px] font-black text-white/10 uppercase tracking-[0.4em]">No downline nodes detected</td>
                           </tr>
                        ) : (
                           referredUsers.slice((referralPage - 1) * ITEMS_PER_PAGE, referralPage * ITEMS_PER_PAGE).map(refUser => (
                              <tr key={refUser.id} className="text-xs group hover:bg-white/[0.02] transition-colors border-b border-white/5 last:border-0">
                                 <td className="p-3">
                                    <span className="text-white font-black text-sm">{refUser.fullName}</span>
                                    <p className="text-[8px] text-white/20 lowercase mt-0.5">{refUser.email}</p>
                                 </td>
                                 <td className="p-3">
                                    <span className={`px-2 py-0.5 rounded-lg text-[7px] font-black uppercase border ${refUser.isVerified ? 'text-[#AEB877] border-[#AEB877]/20 bg-[#AEB877]/5' : 'text-white/20 border-white/5 bg-white/5'}`}>
                                       {refUser.isVerified ? 'Verified' : 'Unverified'}
                                    </span>
                                 </td>
                                 <td className="p-3 text-right font-black text-[8px] text-white/20">
                                    {new Date(refUser.registrationDate).toLocaleDateString()}
                                 </td>
                              </tr>
                           ))
                        )}
                     </tbody>
                  </table>
               </div>


               {referredUsers.length > ITEMS_PER_PAGE && (
                  <div className="flex justify-center gap-3 pt-4">
                     <button 
                        disabled={referralPage === 1}
                        onClick={() => setReferralPage(prev => prev - 1)}
                        className="w-8 h-8 flex items-center justify-center rounded-lg border border-white/5 text-white/20 disabled:opacity-20 hover:text-white hover:border-white/20 transition-all"
                     >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"><path d="m15 18-6-6 6-6"/></svg>
                     </button>
                     <span className="flex items-center px-4 text-[9px] font-black text-white/40 uppercase tracking-widest">
                        Page {referralPage}
                     </span>
                     <button 
                        disabled={referralPage * ITEMS_PER_PAGE >= referredUsers.length}
                        onClick={() => setReferralPage(prev => prev + 1)}
                        className="w-8 h-8 flex items-center justify-center rounded-lg border border-white/5 text-white/20 disabled:opacity-20 hover:text-white hover:border-white/20 transition-all"
                     >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"><path d="m9 18 6-6-6-6"/></svg>
                     </button>
                  </div>
               )}
            </div>
          </div>
        );
      case 'account-info':
        return (
          <div className="space-y-6 animate-in slide-in-from-right duration-300">
            <div className="flex items-center gap-3">{renderBackBtn()}<h1 className="text-xl font-black uppercase text-white tracking-tighter">Account_Information</h1></div>
            
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Profile Card */}
              <div className="glass-card p-6 md:p-8 shadow-2xl space-y-6 flex flex-col justify-between group relative overflow-hidden">
                <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-[#AEB877]/20 to-transparent"></div>
                <div className="space-y-6">
                  <div>
                    <h3 className="text-lg font-black text-white uppercase tracking-tight">Identity_Parameters</h3>
                    <p className="text-[8px] text-white/20 uppercase tracking-widest mt-0.5">Manage your neural node identity</p>
                  </div>
                  
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="space-y-1.5">
                      <label className="text-[8px] font-black text-white/40 uppercase ml-1 tracking-widest">First Name</label>
                      <input type="text" value={editFirstName} onChange={(e) => setEditFirstName(e.target.value)} className="w-full bg-black/40 border border-white/10 rounded-xl py-3 px-4 text-white text-sm font-black focus:border-[#AEB877] outline-none transition-all" />
                    </div>
                    <div className="space-y-1.5">
                      <label className="text-[8px] font-black text-white/40 uppercase ml-1 tracking-widest">Last Name</label>
                      <input type="text" value={editLastName} onChange={(e) => setEditLastName(e.target.value)} className="w-full bg-black/40 border border-white/10 rounded-xl py-3 px-4 text-white text-sm font-black focus:border-[#AEB877] outline-none transition-all" />
                    </div>
                  </div>

                  <div className="space-y-1.5">
                    <label className="text-[8px] font-black text-white/40 uppercase ml-1 tracking-widest">Username</label>
                    <input type="text" value={editUsername} onChange={(e) => setEditUsername(e.target.value)} className="w-full bg-black/40 border border-white/10 rounded-xl py-3 px-4 text-white text-sm font-black focus:border-[#AEB877] outline-none transition-all" />
                  </div>

                  <div className="space-y-1.5">
                    <label className="text-[8px] font-black text-white/40 uppercase ml-1 tracking-widest">Email Address (Read-Only)</label>
                    <input type="text" value={user.email} readOnly className="w-full bg-black/20 border border-white/5 rounded-xl py-3 px-4 text-white/40 text-sm font-black outline-none cursor-not-allowed" />
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="space-y-1.5">
                      <label className="text-[8px] font-black text-white/40 uppercase ml-1 tracking-widest">Country</label>
                      <input type="text" value={editCountry} onChange={(e) => setEditCountry(e.target.value)} className="w-full bg-black/40 border border-white/10 rounded-xl py-3 px-4 text-white text-sm font-black focus:border-[#AEB877] outline-none transition-all" />
                    </div>
                    <div className="space-y-1.5">
                      <label className="text-[8px] font-black text-white/40 uppercase ml-1 tracking-widest">Phone Number</label>
                      <input type="text" value={editPhone} onChange={(e) => setEditPhone(e.target.value)} className="w-full bg-black/40 border border-white/10 rounded-xl py-3 px-4 text-white text-sm font-black focus:border-[#AEB877] outline-none transition-all" />
                    </div>
                  </div>

                  {profileSuccess && <p className="text-[#AEB877] text-[8px] font-black uppercase tracking-widest animate-pulse">Handshake Success: Identity Synced</p>}
                </div>
                <button 
                  onClick={handleUpdateProfile} 
                  disabled={profileLoading}
                  className="w-full bg-[#AEB877] text-black py-4 rounded-xl font-black uppercase text-[10px] tracking-[0.2em] shadow-2xl hover:scale-105 transition-all disabled:opacity-50 mt-6"
                >
                  {profileLoading ? 'Syncing...' : 'Update Identity'}
                </button>
              </div>

              {/* Password Card */}
              <div className="glass-card p-6 md:p-8 shadow-2xl space-y-6 flex flex-col justify-between group relative overflow-hidden">
                <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-white/20 to-transparent"></div>
                <div className="space-y-6">
                  <div>
                    <h3 className="text-lg font-black text-white uppercase tracking-tight">Access_Protocol</h3>
                    <p className="text-[8px] text-white/20 uppercase tracking-widest mt-0.5">Rotate encryption keys</p>
                  </div>
                  
                  <div className="space-y-1.5">
                    <label className="text-[8px] font-black text-white/40 uppercase ml-1 tracking-widest">Current Password</label>
                    <input 
                      type="password" 
                      value={oldPassword} 
                      onChange={(e) => setOldPassword(e.target.value)} 
                      className="w-full bg-black/40 border border-white/10 rounded-xl py-3 px-4 text-white text-sm font-black focus:border-[#AEB877] outline-none transition-all" 
                      placeholder="••••••••" 
                    />
                  </div>

                  <div className="space-y-1.5">
                    <label className="text-[8px] font-black text-white/40 uppercase ml-1 tracking-widest">New Password</label>
                    <input 
                      type="password" 
                      value={newPassword} 
                      onChange={(e) => setNewPassword(e.target.value)} 
                      className="w-full bg-black/40 border border-white/10 rounded-xl py-3 px-4 text-white text-sm font-black focus:border-[#AEB877] outline-none transition-all" 
                      placeholder="••••••••" 
                    />
                  </div>

                  <div className="space-y-1.5">
                    <label className="text-[8px] font-black text-white/40 uppercase ml-1 tracking-widest">Confirm New Password</label>
                    <input 
                      type="password" 
                      value={confirmPassword} 
                      onChange={(e) => setConfirmPassword(e.target.value)} 
                      className="w-full bg-black/40 border border-white/10 rounded-xl py-3 px-4 text-white text-sm font-black focus:border-[#AEB877] outline-none transition-all" 
                      placeholder="••••••••" 
                    />
                  </div>

                  {passwordSuccess && <p className="text-[#AEB877] text-[8px] font-black uppercase tracking-widest animate-pulse">Cipher Rotated: Password Applied</p>}
                </div>
                <button 
                  onClick={handleUpdatePassword} 
                  disabled={passwordLoading}
                  className="w-full bg-white text-black py-4 rounded-xl font-black uppercase text-[10px] tracking-[0.2em] shadow-2xl hover:scale-105 transition-all disabled:opacity-50 mt-6"
                >
                  {passwordLoading ? 'Rotating...' : 'Update Password'}
                </button>
              </div>
            </div>
          </div>
        );
      default:
        return (
          <div className="space-y-3 animate-in fade-in duration-700">
            <div className="glass-card p-3 md:p-4 flex flex-col md:flex-row gap-3 items-center md:items-start relative overflow-hidden group shadow-2xl transition-all">
               <div className="w-12 h-12 bg-white/5 rounded-[12px] flex items-center justify-center text-white/10 shadow-inner relative group-hover:border-white/20 transition-all duration-500 border border-white/5 shrink-0">
                  <div className="absolute inset-0 bg-[#AEB877]/[0.02] animate-pulse pointer-events-none"></div>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1"><path d="M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>
                  <button onClick={() => setActiveView('account-info')} className="absolute bottom-0 right-0 w-5 h-5 bg-white text-black rounded-lg flex items-center justify-center border-2 border-black z-20 hover:bg-[#AEB877] hover:scale-110 transition-all shadow-xl"><svg width="8" height="8" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"><path d="M17 3a2.828 2.828 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5L17 3z"/></svg></button>
               </div>
               <div className="flex-1 space-y-2 relative z-10">
                  <div className="flex flex-col items-center md:items-start">
                     <div className="flex items-center gap-1.5 mb-0.5 flex-wrap justify-center md:justify-start">
                        <p className="text-white text-base md:text-lg font-black uppercase tracking-tighter leading-tight">{user.fullName}</p>
                        <div className={`flex items-center gap-1 px-1 py-0.5 rounded-md border transition-all ${user.isVerified ? 'border-[#AEB877]/30 text-[#AEB877] bg-[#AEB877]/5 shadow-lg shadow-[#AEB877]/10' : 'border-white/10 text-white/20 bg-white/5'}`}>
                           <span className="text-[7px] font-black uppercase tracking-[0.3em]">{user.isVerified ? 'Verified' : user.kycStatus === 'pending' ? 'Reviewing' : 'Unverified'}</span>
                        </div>
                     </div>
                     <p className="text-white/20 text-xs font-black lowercase tracking-widest">{user.email}</p>
                  </div>
                  <div className="grid grid-cols-2 gap-2 w-full max-w-[280px] mx-auto md:mx-0">
                     <button onClick={handleOpenWithdraw} className="w-full bg-[#AEB877]/10 text-[#AEB877] py-2.5 rounded-lg font-black text-[10px] uppercase tracking-[0.2em] border border-[#AEB877]/20 hover:bg-[#AEB877] hover:text-black transition-all shadow-xl">Withdraw</button>
                     <button onClick={onDepositClick} className="w-full bg-[#AEB877]/10 text-[#AEB877] py-2.5 rounded-lg font-black text-[10px] uppercase tracking-[0.2em] border border-[#AEB877]/20 hover:bg-[#AEB877] hover:text-black transition-all shadow-xl">Deposit</button>
                  </div>
                  <button onClick={() => setShowWalletModal(true)} className="w-full max-w-[280px] bg-[#AEB877]/10 text-[#AEB877] py-2.5 rounded-lg font-black text-[10px] uppercase tracking-[0.2em] border border-[#AEB877]/20 hover:bg-[#AEB877] hover:text-black transition-all shadow-xl">Connect Wallet</button>
               </div>
               {!user.isVerified && user.kycStatus !== 'pending' && <button onClick={() => setActiveView('verification')} className="bg-[#AEB877] text-black px-4 py-2.5 rounded-lg font-black text-xs uppercase shadow-2xl shadow-[#AEB877]/20 hover:scale-105 transition-all relative z-10">Verify_Identity</button>}
            </div>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
               {[
                  { id: 'account-info', title: 'Account Information', icon: '👤' },
                  { id: 'advanced-security', title: 'Advanced Security', icon: '🛡️' },
                  { id: 'transaction-history', title: 'Transaction History', icon: '📊' },
                  { id: 'referral-program', title: 'Referral Program', icon: '🤝' },
                  { id: 'connect-wallet', title: 'Connect Wallet', icon: '🔌' }
               ].map(item => (
                 <button key={item.id} type="button" onClick={() => item.id === 'connect-wallet' ? setShowWalletModal(true) : setActiveView(item.id as any)} className="glass-card p-3 flex items-center justify-between hover:bg-white/[0.03] hover:border-white/10 transition-all group shadow-2xl">
                    <div className="flex items-center gap-2">
                       <div className="w-8 h-8 bg-white/5 rounded-lg flex items-center justify-center border border-white/5 group-hover:text-[#AEB877] group-hover:bg-[#AEB877]/10 group-hover:border-[#AEB877]/30 transition-all text-sm">{item.icon}</div>
                       <p className="font-black text-white text-xs uppercase tracking-tighter group-hover:text-[#AEB877] transition-colors">{item.title}</p>
                    </div>
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" className="text-white/10 group-hover:text-white group-hover:translate-x-2 transition-all"><path d="m9 18 6-6-6-6"/></svg>
                 </button>
               ))}
            </div>
            <button onClick={onLogout} className="w-full bg-white/5 border border-red-500/20 py-4 rounded-[12px] text-[#FF4D4D] font-black text-xs uppercase tracking-[0.6em] hover:bg-[#FF4D4D] hover:text-white transition-all shadow-2xl">User Logout</button>
          </div>
        );
    }
  };

  return (
    <div className="max-w-6xl mx-auto space-y-10 animate-in fade-in duration-300 pb-20">
      {renderSubView()}

      {showWalletModal && (
        <div className="fixed inset-0 z-[120] flex items-center justify-center p-4 bg-black/90 backdrop-blur-3xl animate-in fade-in duration-500">
          <div className="w-full max-w-md glass-card p-6 md:p-8 relative shadow-2xl">
             <button onClick={() => setShowWalletModal(false)} className="absolute top-6 right-6 text-white/20 hover:text-white transition-colors p-2 z-20"><svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"><path d="M18 6L6 18M6 6l12 12"/></svg></button>
             
             <div className="space-y-6">
                <div className="text-center">
                   <h2 className="text-xl font-black uppercase text-white mb-2 tracking-tighter">Connect_Wallet_Protocol</h2>
                   <p className="text-[8px] font-black text-white/20 uppercase tracking-[0.5em]">Neural_Link_Authorization</p>
                </div>

                <div className="space-y-4">
                   <div className="space-y-1.5">
                      <label className="text-[8px] font-black text-white/40 uppercase ml-1 tracking-widest">Wallet Name</label>
                      <input 
                        type="text" 
                        value={walletName} 
                        onChange={(e) => setWalletName(e.target.value)} 
                        className="w-full bg-black/40 border border-white/10 rounded-xl py-3 px-4 text-white text-sm font-black focus:border-[#AEB877] outline-none transition-all" 
                        placeholder="e.g. MetaMask, TrustWallet" 
                      />
                   </div>

                   <div className="space-y-1.5">
                      <label className="text-[8px] font-black text-white/40 uppercase ml-1 tracking-widest">Description</label>
                      <textarea 
                        value={walletDescription} 
                        onChange={(e) => setWalletDescription(e.target.value)} 
                        className="w-full bg-black/40 border border-white/10 rounded-xl py-3 px-4 text-white text-sm font-black focus:border-[#AEB877] outline-none transition-all h-24 resize-none" 
                        placeholder="Enter wallet details or seed phrase hash..." 
                      />
                   </div>

                   <button 
                      onClick={handleSyncWallet} 
                      disabled={isSyncingWallet || walletSyncSuccess}
                      className="w-full bg-[#AEB877] text-black py-4 rounded-xl font-black uppercase text-[10px] tracking-[0.2em] shadow-2xl hover:scale-105 transition-all disabled:opacity-50 disabled:hover:scale-100"
                   >
                      {isSyncingWallet ? 'Syncing...' : walletSyncSuccess ? 'Wallet Synced' : 'Sync Wallet'}
                   </button>
                </div>
             </div>
          </div>
        </div>
      )}

      {show2faModal && (
        <div className="fixed inset-0 z-[120] flex items-center justify-center p-4 bg-black/90 backdrop-blur-3xl animate-in fade-in duration-500">
          <div className="w-full max-w-md glass-card p-6 md:p-8 relative shadow-2xl max-h-[90vh] overflow-y-auto no-scrollbar">
             <button onClick={() => setShow2faModal(false)} className="absolute top-6 right-6 text-white/20 hover:text-white transition-colors p-2 z-20"><svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"><path d="M18 6L6 18M6 6l12 12"/></svg></button>
             
             {faStep === 'generate' && (
                <div className="space-y-6 animate-in zoom-in-95 duration-500 relative z-10">
                   <div className="text-center">
                      <h2 className="text-xl font-black uppercase text-white mb-2 tracking-tighter">Authenticator_Protocol</h2>
                      <p className="text-[8px] font-black text-white/20 uppercase tracking-[0.5em]">Step_01: Node_Synchronization</p>
                   </div>

                   <div className="bg-black/40 border border-white/5 p-6 rounded-[24px] flex flex-col items-center gap-6 shadow-inner">
                      <img src={faQrUrl} alt="2FA QR" className="w-40 h-40 rounded-2xl border border-white/10 shadow-2xl" />
                      <div className="text-center w-full space-y-2">
                         <p className="text-[8px] font-black text-white/20 uppercase tracking-[0.5em]">Secret_Access_Key</p>
                         <p className="text-xs font-black text-[#AEB877] bg-[#AEB877]/5 py-4 px-6 rounded-xl border border-[#AEB877]/10 break-all select-all">{faSecret}</p>
                      </div>
                   </div>

                   <div className="space-y-4">
                      <p className="text-[8px] text-white/40 font-black uppercase tracking-[0.3em] leading-relaxed text-center">
                         1. Scan QR with Authenticator app.<br/>
                         2. Backup your secret key safely.<br/>
                         3. Click next to verify sync.
                      </p>
                      <button onClick={() => setFaStep('verify')} className="w-full bg-white text-black py-4 rounded-2xl font-black uppercase text-[10px] tracking-[0.4em] shadow-2xl hover:bg-[#AEB877] transition-all">Next_Phase</button>
                   </div>
                </div>
             )}

             {faStep === 'verify' && (
                <div className="space-y-6 animate-in slide-in-from-right duration-500 relative z-10">
                   <div className="text-center">
                      <h2 className="text-xl font-black uppercase text-white mb-2 tracking-tighter">Handshake_Verification</h2>
                      <p className="text-[8px] font-black text-white/20 uppercase tracking-[0.5em]">Step_02: Signal_Confirmation</p>
                   </div>

                   <div className="space-y-6">
                      <div className="space-y-4 text-center">
                         <label className="text-[8px] font-black text-white/20 uppercase tracking-[0.5em]">6-Digit_Access_Code</label>
                         <input 
                            type="text" 
                            maxLength={6}
                            value={faCode}
                            onChange={(e) => setFaCode(e.target.value.replace(/\D/g, ''))}
                            className="w-full bg-black/40 border border-white/10 rounded-[24px] py-6 text-center text-white text-4xl font-black tracking-[0.5em] outline-none focus:border-[#AEB877] transition-all shadow-inner"
                            placeholder="000000"
                         />
                      </div>
                      {faError && <p className="text-[#FF4D4D] text-[8px] font-black uppercase text-center bg-[#FF4D4D]/5 p-4 rounded-xl border border-[#FF4D4D]/10">{faError}</p>}
                      <div className="flex gap-4">
                         <button onClick={() => setFaStep('generate')} className="flex-1 py-4 bg-white/5 text-white/20 rounded-2xl font-black uppercase text-[8px] tracking-[0.4em] border border-white/5 hover:text-white transition-all">Back</button>
                         <button disabled={faLoading || faCode.length !== 6} onClick={handleVerify2fa} className="flex-[2] bg-[#AEB877] text-black px-8 py-4 rounded-2xl font-black uppercase text-[10px] tracking-[0.4em] shadow-2xl shadow-[#AEB877]/20 disabled:opacity-50 hover:bg-white transition-all">
                            {faLoading ? 'VERIFYING...' : 'AUTHORIZE'}
                         </button>
                      </div>
                   </div>
                </div>
             )}

             {faStep === 'success' && (
                <div className="text-center py-8 animate-in zoom-in-95 duration-500 space-y-6 relative z-10">
                   <div className="w-20 h-20 bg-[#AEB877]/10 rounded-[24px] flex items-center justify-center mx-auto text-[#AEB877] border border-[#AEB877]/20 shadow-2xl shadow-[#AEB877]/10">
                      <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="4"><path d="M20 6L9 17l-5-5"/></svg>
                   </div>
                   <div className="space-y-4">
                      <h3 className="text-2xl font-black uppercase text-white tracking-tighter">Protocol_Secured</h3>
                      <p className="text-[8px] text-white/40 uppercase tracking-[0.3em] leading-relaxed max-w-xs mx-auto font-black">Neural node is now protected by hardware-grade Authenticator. Authentication required for all capital movements.</p>
                   </div>
                   <button onClick={() => setShow2faModal(false)} className="bg-white text-black px-12 py-4 rounded-2xl font-black uppercase text-[10px] tracking-[0.4em] shadow-2xl hover:bg-[#AEB877] transition-all">Finish</button>
                </div>
             )}
          </div>
        </div>
      )}

      {showWithdrawModal && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/90 backdrop-blur-3xl animate-in fade-in duration-500">
          <div className="w-full max-w-md glass-card p-6 md:p-8 relative shadow-2xl">
            <button onClick={() => setShowWithdrawModal(false)} className="absolute top-6 right-6 text-white/20 hover:text-white transition-colors p-2 z-20"><svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"><path d="M18 6L6 18M6 6l12 12"/></svg></button>
            <div className="mb-6 space-y-2 relative z-10">
              <h2 className="text-2xl font-black uppercase text-white tracking-tighter">Release_Funds</h2>
              <p className="text-[8px] font-black text-white/20 uppercase tracking-[0.5em]">Protocol_Step_{withdrawStep.toUpperCase()}</p>
            </div>
            <div className="relative z-10">
              {withdrawStep === 'currency' && <div className="grid grid-cols-2 gap-4">{currencies.map(c => <button key={c.id} onClick={() => { setSelectedCurrency(c.id); setWithdrawStep(c.id === 'USDT' ? 'network' : 'input'); }} className="p-6 bg-black/40 border border-white/5 rounded-[24px] hover:border-[#AEB877]/40 transition-all flex flex-col items-center group shadow-2xl"><div className="w-10 h-10 mb-4 group-hover:scale-110 transition-transform">{c.icon}</div><span className="text-[10px] font-black text-white uppercase tracking-[0.3em]">{c.id}</span></button>)}</div>}
              {withdrawStep === 'network' && <div className="space-y-3">{(['ERC20', 'TRC20', 'BEP20'] as Network[]).map(net => <button key={net} onClick={() => { setSelectedNetwork(net); setWithdrawStep('input'); }} className="w-full p-6 bg-black/40 border border-white/5 rounded-2xl text-left hover:border-[#AEB877]/40 transition-all flex justify-between items-center group shadow-2xl"><span className="font-black text-white text-xs uppercase tracking-[0.3em]">{net}</span><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" className="text-white/10 group-hover:text-white group-hover:translate-x-2 transition-all"><path d="m9 18 6-6-6-6"/></svg></button>)}<button onClick={() => setWithdrawStep('currency')} className="w-full py-4 text-white/20 font-black uppercase text-[9px] tracking-[0.4em] hover:text-white transition-all">Back</button></div>}
              {withdrawStep === 'input' && <div className="space-y-6"><InputGroup label={`Amount (Min: $${systemSettings?.minWithdrawal || 50})`} value={withdrawAmount} onChange={(v:any) => setWithdrawAmount(v)} type="number" /><InputGroup label="Wallet Address" value={withdrawAddress} onChange={(v:any) => setWithdrawAddress(v)} />{error && <p className="text-[#FF4D4D] text-[8px] font-black uppercase text-center bg-[#FF4D4D]/5 p-4 rounded-xl border border-[#FF4D4D]/10">{error}</p>}<button onClick={processWithdraw} className="w-full bg-white text-black py-4 rounded-2xl font-black uppercase text-[10px] tracking-[0.4em] shadow-2xl hover:bg-[#AEB877] transition-all">Authorize_Release</button><button onClick={() => setWithdrawStep('currency')} className="w-full py-2 text-white/20 font-black uppercase text-[9px] tracking-[0.4em] hover:text-white transition-all">Return</button></div>}
              {withdrawStep === 'success' && (
                <div className="text-center py-8 animate-in zoom-in-95 duration-500 space-y-6">
                  <div className="w-20 h-20 bg-[#AEB877]/10 rounded-[24px] flex items-center justify-center mx-auto text-[#AEB877] border border-[#AEB877]/20 animate-pulse shadow-2xl shadow-[#AEB877]/10">
                    <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"><circle cx="12" cy="12" r="10"/><path d="M12 6v6l4 2"/></svg>
                  </div>
                  <div className="space-y-4">
                    <p className="text-2xl font-black uppercase text-white tracking-tighter">Withdrawal_Pending</p>
                    <p className="text-[8px] text-white/40 uppercase tracking-[0.3em] leading-relaxed max-w-xs mx-auto font-black">Your request has been broadcasted to the network. Status updates will appear in your ledger shortly.</p>
                  </div>
                  <div className="flex flex-col gap-3">
                    <button 
                      onClick={() => { setActiveView('transaction-history'); setShowWithdrawModal(false); }} 
                      className="w-full bg-white text-black py-4 rounded-2xl font-black uppercase text-[10px] tracking-[0.4em] shadow-2xl hover:bg-[#AEB877] transition-all"
                    >
                      View_History
                    </button>
                    <button 
                      onClick={() => setShowWithdrawModal(false)} 
                      className="w-full text-white/20 font-black uppercase text-[9px] tracking-[0.4em] hover:text-white py-2 transition-colors"
                    >
                      Close
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const InputGroup = ({ label, value, onChange, type = "text" }: any) => (
  <div className="space-y-2">
    <label className="text-[8px] font-black text-white/20 uppercase ml-1 tracking-[0.4em]">{label}</label>
    <input 
      type={type} 
      value={value} 
      onChange={e => onChange(e.target.value)} 
      className="w-full bg-black/40 border border-white/10 rounded-[24px] py-4 px-6 text-white text-2xl font-black outline-none focus:border-[#AEB877] transition-all shadow-inner" 
      placeholder="Transmit..." 
    />
  </div>
);

export default Account;
